import { z } from "zod";
import { ActorId, CountryCode, IsoUtc, Uuid } from "./primitives";

/**
 * Private one-to-one messages (ADR-0014; founder decision 2026-10-13). Between verified accounts only; a recipient can
 * accept messages from anyone with a verified account or from nobody; a mute (`/me/blocks`) stops messages both ways;
 * text passes the language filter and stays plain text; messages are kept DM_RETENTION_DAYS (30) unless reported;
 * moderators read a conversation only when someone reports it.
 */
export const DM_MAX_CHARS = 500;

export const DmPolicy = z.enum(["everyone", "nobody"]);
export type DmPolicy = z.infer<typeof DmPolicy>;

export const DmParticipant = z.object({ actorId: ActorId, label: z.string(), countryCode: CountryCode.nullable() });
export type DmParticipant = z.infer<typeof DmParticipant>;

export const DmMessageView = z.object({
  messageId: Uuid,
  conversationId: Uuid,
  fromMe: z.boolean(),
  text: z.string(),
  at: IsoUtc,
});
export type DmMessageView = z.infer<typeof DmMessageView>;

export const ConversationView = z.object({
  conversationId: Uuid,
  other: DmParticipant,
  lastMessage: z.object({ text: z.string(), at: IsoUtc, fromMe: z.boolean() }).nullable(),
  unreadCount: z.number().int().min(0),
  updatedAt: IsoUtc,
  /** Whether you can send here now, and why not (muted either way, they accept no messages, ...). */
  canSend: z.boolean(),
  cannotSendReason: z.string().nullable(),
  /** You muted them (their messages are hidden from your list's unread count and notices). */
  mutedByMe: z.boolean(),
});
export type ConversationView = z.infer<typeof ConversationView>;

export const ConversationListResponse = z.object({ items: z.array(ConversationView), unreadTotal: z.number().int().min(0) });
export type ConversationListResponse = z.infer<typeof ConversationListResponse>;

/** POST /me/conversations: open (or create) the conversation with a person. */
export const OpenConversationRequest = z.object({ recipientActorId: ActorId }).strict();
export type OpenConversationRequest = z.infer<typeof OpenConversationRequest>;

export const MessagePageQuery = z.object({ before: z.string().max(200).optional(), limit: z.coerce.number().int().min(1).max(100).optional() });
/** Oldest first; `nextBefore` loads older messages. */
export const MessagePage = z.object({ items: z.array(DmMessageView), nextBefore: z.string().nullable() });
export type MessagePage = z.infer<typeof MessagePage>;

export const SendMessageRequest = z
  .object({
    text: z.string().min(1).max(DM_MAX_CHARS + 20),
    /** Client-generated id: a retried send returns the same message. */
    clientMessageId: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/),
  })
  .strict();
export type SendMessageRequest = z.infer<typeof SendMessageRequest>;
export const SendMessageResponse = z.object({ message: DmMessageView, masked: z.boolean(), duplicate: z.boolean() });
export type SendMessageResponse = z.infer<typeof SendMessageResponse>;

export const DmSettings = z.object({ policy: DmPolicy });
export type DmSettings = z.infer<typeof DmSettings>;

/** Realtime: "you have a new private message" (no content travels through the rooms). */
export const DmNotice = z.object({ conversationId: Uuid });
export type DmNotice = z.infer<typeof DmNotice>;
